import qs, { stringify } from "qs";
import { getHeaders, makeRequest } from "$utils/utils";
import config from "./config";
import { parseStrapiFormat } from "$utils/strapi";
import { Services, SERVICES_URL } from "$utils/typeConstants";

const { order_url: url } = config;

export enum ERRORS {
  UNKNOWN_ERROR = "Une erreur inconnue est survenue.",
  MISSING_FIELD = "Donnée manquante !",
}

// In Reception.service.ts
export const getReceptionsFromGateway = async (
  strapiQuery: string,
  token: string
) => {
  try {
    // Fix the URL path to match the one in your gateway route configuration
    const gatewayUrl = `${
      SERVICES_URL[Services.GATEWAY_API]
    }/api/order/receptions/consolidated`;

    // Make sure we're passing the correct headers with the token
    const headers = getHeaders(token);

    // Use the makeRequest function with the correct parameters
    const response = await makeRequest(
      `${gatewayUrl}?${strapiQuery}`,
      "GET",
      undefined,
      headers
    );

    if (!response?.data) {
      throw new Error(ERRORS.UNKNOWN_ERROR);
    }

    return {
      data: response.data.data,
      companies: response.data.companies,
      warehouses: response.data.warehouses,
      dockyards: response.data.dockyards,
      projects: response.data.projects,
    };
  } catch (error: any) {
    console.error("Gateway request error:", error);
    return { error };
  }
};

export const getReceptionById = async (id) => {
  // Note: Prisma API automatically includes related data, no populate needed
  const res = await makeRequest(
    `${url}/receptions/${id}`,
    "GET",
    undefined,
    getHeaders("")
  );

  return parseStrapiFormat(res.data);
};

export const getOrderById = async (id) => {
  // Note: Prisma API automatically includes related data, no populate needed
  const res = await makeRequest(
    `${url}/orders/${id}`,
    "GET",
    undefined,
    getHeaders("")
  );
  return parseStrapiFormat(res.data);
};

export const getReceptionQuantity = async (data: any) => {
  try {
    // Backwards-compatible wrapper: prefer GET minimal payload when possible.
    const company = data?.company;
    const orderIds = Array.isArray(data?.orderIds)
      ? data.orderIds
      : Array.isArray(data?.orders)
      ? data.orders.map((o: any) => o?.id || o?._id).filter(Boolean)
      : [];

    if (company && Array.isArray(orderIds) && orderIds.length > 0) {
      return await getReceptionQuantityForOrders({ company, orderIds });
    }

    // No valid input for quantity computation
    throw new Error(ERRORS.MISSING_FIELD);
  } catch (err) {
    const error = (err as any)?.response?.data?.message ?? err;
    return { error };
  }
};

export const getReceptionQuantityForOrders = async (params: {
  company: string;
  orderIds: Array<string | number>;
}) => {
  try {
    const ids = (params.orderIds || []).filter(Boolean).join(",");
    const res = await makeRequest(
      `${url}/receptions/quantity?company=${encodeURIComponent(
        params.company
      )}&orderIds=${encodeURIComponent(ids)}`,
      "GET"
    );
    if (!res || !res.data || !res.status) {
      throw new Error(ERRORS.UNKNOWN_ERROR);
    }
    return res.data;
  } catch (err) {
    const error = (err as any)?.response?.data?.message ?? err;
    return { error };
  }
};

export const createReceptionItem = async (body: any) => {
  try {
    const res = await makeRequest(`${url}/receptions`, "POST", body);
    return res.data;
  } catch (error: any) {
    return { error };
  }
};

export const updateReceptionItem = async (id: string, body: any) => {
  try {
    const res = await makeRequest(`${url}/receptions/${id}`, "PUT", body);
    return res.data;
  } catch (error: any) {
    return { error };
  }
};

export const getReceptionItem = async (id: number) => {
  try {
    // Note: Prisma API automatically includes related data, no populate needed
    const res = await makeRequest(`${url}/receptions/${id}`, "GET");
    // Handle both old Strapi structure (data.data) and new Prisma structure (data directly)
    return res.data?.data || res.data;
  } catch (error: any) {
    return { error };
  }
};

export const deleteReceptionItem = async (id: string) => {
  try {
    const res = await makeRequest(
      `${url}/receptions/${id}`,
      "DELETE",
      undefined
    );
    // Handle both old Strapi structure (data.data) and new Prisma structure (data directly)
    return res.data?.data || res.data;
  } catch (error: any) {
    return { error };
  }
};

export const isProductInAReception = async (
  productReference: string,
  excludedReceptionId?: number,
  companyId?: string
) => {
  const queryParams = qs.stringify(
    {
      productReference,
      ...(companyId && { companyId }),
      ...(excludedReceptionId !== undefined && { excludedReceptionId }),
      rowsPerPage: 1,
    },
    {
      encodeValuesOnly: true,
    }
  );

  try {
    const res = await makeRequest(
      `${url}/receptions?${queryParams}`,
      "GET",
      undefined,
      getHeaders("")
    );
    if (!res || !res.data || !res.status) {
      throw new Error("An error occured");
    }
    const data = res.data?.data || res.data;
    if (!Array.isArray(data) || data.length === 0) {
      return false;
    }
    return data.some((reception: any) =>
      reception.receptionReferences?.some(
        (ref: any) =>
          ref.productReference === productReference &&
          (!companyId || ref.companyId === companyId)
      )
    );
  } catch (err) {
    return false;
  }
};

export const pushReception = async (token: string, body: any) => {
  const response = await makeRequest(
    `${url}/stock/receptions`,
    "POST",
    body,
    getHeaders(token)
  );

  return response;
};

export const syncReception = async (token: string, body: any) => {
  const response = await makeRequest(
    `${url}/stock/receptions/pull`,
    "POST",
    body,
    getHeaders(token)
  );

  return response;
};

export const getReferencements = async (
  companyId?: string,
  additionalFilters?: any
) => {
  const request = `${url}/receptions/referencements`;
  const response = await makeRequest(
    `${request}${companyId ? `/${companyId}` : ""}?${stringify(
      additionalFilters,
      {
        arrayFormat: "brackets",
      }
    )}`,
    "GET"
  );
  return response.data;
};

/**
 * @param query : Prisma format query string
 */
export const exportReceptions = async (token: string, query: string) => {
  try {
    const headersWithToken = getHeaders(token);
    const response = await fetch(`${url}/receptions/export`, {
      method: "POST",
      headers: headersWithToken,
      body: JSON.stringify({ query }),
    });
    if (!response.ok) {
      const error = await response.json();
      return { ok: false, error };
    }
    return response;
  } catch (error: any) {
    return { error };
  }
};

/**
 * Check which suborder references have associated receptions
 * @param suborderReferences - Array of suborder reference strings to check
 * @param token - Auth token
 * @returns Object with subordersWithReceptions array
 */
export const checkSuborderReceptions = async (
  suborderReferences: string[],
  token: string
): Promise<{ subordersWithReceptions: string[] }> => {
  try {
    if (!suborderReferences || suborderReferences.length === 0) {
      return { subordersWithReceptions: [] };
    }

    const gatewayUrl = `${
      SERVICES_URL[Services.GATEWAY_API]
    }/api/order/receptions/checkSuborderReceptions`;
    const headers = getHeaders(token);

    const response = await makeRequest(
      gatewayUrl,
      "POST",
      { suborderReferences },
      headers
    );

    return response.data || { subordersWithReceptions: [] };
  } catch (error: any) {
    console.error("Error checking suborder receptions:", error);
    return { subordersWithReceptions: [] };
  }
};
